'use client';

import type { Components } from 'react-markdown';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { MermaidPreview } from '@/lib/tiptap/extensions/MermaidCodeBlock';
import { buildEntityGoHrefFromRefHref, isInternalEntityRefHref } from '@/lib/entityGoLink';

type MarkdownViewerProps = {
  content: string;
  className?: string;
};

// 내부 엔티티 참조(plan:<id> 등) href는 sanitization에 의해 제거되지 않도록 그대로 보존한다.
const urlTransform = (url: string): string => (isInternalEntityRefHref(url) ? url : defaultUrlTransform(url));

export const MarkdownViewer = ({ content, className }: MarkdownViewerProps) => {
  const components: Components = {
    a({ children, href, node: _node, ...props }) {
      void _node;
      const goHref = href ? buildEntityGoHrefFromRefHref(href) : null;
      if (goHref) {
        return (
          <a
            {...props}
            href={goHref}
            onClick={(e) => {
              e.preventDefault();
              window.open(goHref, '_blank', 'noopener,noreferrer');
            }}
          >
            {children}
          </a>
        );
      }
      return (
        <a {...props} href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    },
    code({ className, children }) {
      const match = typeof className === 'string' ? /language-(\w+)/.exec(className) : null;
      if (match?.[1] === 'mermaid') {
        const source = String(children).replace(/\n$/, '');
        return <MermaidPreview source={source} />;
      }
      const text = String(children).replace(/\n$/, '');
      if (!className && /^https?:\/\/\S+$/.test(text)) {
        return (
          <code className={className}>
            <a
              href={text}
              target="_blank"
              rel="noopener noreferrer"
              className="link link-primary no-underline hover:underline"
            >
              {text}
            </a>
          </code>
        );
      }
      return <code className={className}>{children}</code>;
    },
  };

  return (
    <div className={`prose prose-sm max-w-none text-base-content sm:prose-base ${className ?? ''}`.trim()}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components} urlTransform={urlTransform}>
        {content}
      </ReactMarkdown>
    </div>
  );
};
